/**
 * The account statement - what has passed between us and this customer.
 *
 * Perfex called this "Account summary" and it was the page a customer's
 * bookkeeper opened once a quarter: every invoice, every payment and every
 * credit note on one running balance, over a window they choose. The invoices
 * tab answers "what do I owe on this document"; this one answers "what does my
 * ledger say against yours", and the two are different questions asked by
 * different people at different desks.
 *
 * ── NOTHING ON THIS PAGE IS COMPUTED HERE ──────────────────────────────────
 *
 * Every figure - the opening balance, the two period totals, the closing balance
 * and the balance on each row - arrives from the server and is printed as it
 * came. Summing the rows in the browser to double-check would be a second
 * implementation of the ledger, and a second implementation is a second thing
 * that can be wrong with nothing to check it against; when the two disagreed,
 * the page would show whichever one it happened to render. The arithmetic has
 * one home (`apps/api/src/modules/portal/lib/statement.ts`, pure and tested) and
 * this file is a view of it.
 *
 * ── THE URL IS THE VIEW ────────────────────────────────────────────────────
 *
 * Period and company live in `?period=` and `?client=` rather than in state
 * alone, so the reader can send "our 2025 statement" to their accountant as a
 * link and reload without losing the window. Every rewrite goes through
 * `keepPreview`, because `setParams` replaces the whole query string and a staff
 * preview would otherwise drop its token on the first click and empty itself.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertCircle, Printer } from "lucide-react";
import {
  Button, Card,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@wa/ui";
import { useT, useI18n } from "../../i18n/I18nProvider";
import { keepPreview } from "../../lib/previewMode";
import { portal, type PortalStatement, type PortalStatementRow } from "../../lib/portal";
import { fmtDate, fmtAmount } from "../../lib/format";
import { EmptyState, Figure, ListSkeleton, PageHead } from "../ui";
import {
  STATEMENT_PERIODS, periodWindow, readPeriod, type StatementPeriod,
} from "./statementPeriods";

type Translate = ReturnType<typeof useT>;

/** The i18n key for a period's name in the picker. */
const PERIOD_LABEL: Record<StatementPeriod, string> = {
  "this-year": "portal.statement.periodThisYear",
  "last-year": "portal.statement.periodLastYear",
  "last-12-months": "portal.statement.periodLast12Months",
  all: "portal.statement.periodAll",
};

export function StatementSection() {
  const t = useT();
  const { locale } = useI18n();
  const [params, setParams] = useSearchParams();

  const period = readPeriod(params.get("period"));
  const clientId = params.get("client") ?? undefined;

  const [data, setData] = useState<PortalStatement | null>(null);
  const [failed, setFailed] = useState(false);

  // Recomputed only when the period name changes. `new Date()` is read here
  // rather than inside the effect so the dependency list stays a pair of plain
  // strings: a fresh Date object on every render would make the effect re-fire
  // forever and the page would refetch itself in a loop.
  const range = useMemo(() => periodWindow(period, new Date()), [period]);

  useEffect(() => {
    let alive = true;
    setData(null);
    setFailed(false);
    portal
      .statement({ clientId, from: range.from, to: range.to })
      .then((res) => {
        if (alive) setData(res);
      })
      .catch(() => {
        // A statement that half-loads is worse than one that says it did not.
        // Leaving stale rows under a new period caption would be a balance
        // attached to the wrong window, which is the one mistake this page
        // cannot afford.
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [clientId, range.from, range.to]);

  /**
   * Rewrite the query, keeping this tab open.
   *
   * `setParams` REPLACES the query string, so `tab` has to be restated or the
   * shell falls back to the overview mid-click. The company is only written when
   * there is one to write: an empty `?client=` would be a parameter the server
   * has to interpret, and "unset" already means "the first company you may
   * read".
   */
  function apply(next: { period?: StatementPeriod; client?: string }) {
    const query: Record<string, string> = {
      tab: "statement",
      period: next.period ?? period,
    };
    const company = next.client ?? clientId;
    if (company) query.client = company;
    setParams(keepPreview(query));
  }

  const currency = data?.currency ?? "EUR";

  return (
    <div>
      <PageHead
        title={t("portal.statement.title")}
        description={t("portal.statement.lead")}
        actions={
          // Controls, hidden on paper (`PageHead` does that): a printed
          // statement is a document, and a dropdown rendered flat into it only
          // raises the question of what it said when the sheet was printed. The
          // caption above the totals carries the window instead.
          <>
            {/* Drawn only when the reader represents more than one company. A
                statement is a statement of ONE account, so a picker with a
                single entry offers a choice that does not exist. */}
            {data && data.clients.length > 1 && (
              <Select value={data.client.id} onValueChange={(v) => apply({ client: v })}>
                <SelectTrigger className="w-full sm:w-56" aria-label={t("portal.statement.company")}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {data.clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Select value={period} onValueChange={(v) => apply({ period: readPeriod(v) })}>
              <SelectTrigger className="w-full sm:w-48" aria-label={t("portal.statement.period")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATEMENT_PERIODS.map((p) => (
                  <SelectItem key={p} value={p}>{t(PERIOD_LABEL[p])}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button variant="outline" onClick={() => window.print()}>
              <Printer /> {t("portal.statement.print")}
            </Button>
          </>
        }
      />

      {failed ? (
        <Card>
          <EmptyState icon={AlertCircle} title={t("portal.statement.error")} />
        </Card>
      ) : !data ? (
        <Card className="overflow-hidden">
          <ListSkeleton rows={5} label={t("common.loading")} />
        </Card>
      ) : (
        <div className="space-y-4">
          <div>
            <p className="font-semibold">{data.client.name}</p>
            {/* The window comes back from the server, not from the picker.
                The route clamps a start older than twenty years, so echoing
                what was asked for could caption the totals with a period
                they do not cover. */}
            <p className="text-sm tabular-nums text-foreground/70">
              {t("portal.statement.range", {
                from: fmtDate(data.from, locale),
                to: fmtDate(data.to, locale),
              })}
            </p>
          </div>

          <StatementTotals data={data} locale={locale} t={t} />
          <Card className="overflow-hidden">
            <StatementLedger data={data} locale={locale} t={t} currency={currency} />
          </Card>
        </div>
      )}
    </div>
  );
}

/**
 * The four figures, with the one the reader came for held apart.
 *
 * Opening, invoiced and paid are context; the closing balance is the answer, so
 * it gets its own tone and its own line of prose. A row of four identical tiles
 * would make the reader work out which number is the point.
 */
function StatementTotals({ data, locale, t }: { data: PortalStatement; locale: string; t: Translate }) {
  const closing = data.closingCents;
  const inCredit = closing < 0;

  return (
    // One surface cut into four by hairlines (the gaps show the border colour
    // under the cells), rather than four cards in a row: the three context
    // figures and the answer read as one statement, not as four equal tiles.
    <Card className="grid gap-px overflow-hidden bg-border sm:grid-cols-2 lg:grid-cols-4">
      <Figure className="bg-card p-5" label={t("portal.statement.opening")} value={signedMoney(data.openingCents, data.currency, locale, t)} />
      <Figure className="bg-card p-5" label={t("portal.statement.invoiced")} value={fmtAmount(data.invoicedCents, data.currency, locale)} />
      <Figure className="bg-card p-5" label={t("portal.statement.paid")} value={fmtAmount(data.paidCents, data.currency, locale)} />

      {/* A negative closing balance is money in the customer's favour, and it is
          spelled out in words rather than printed with a minus sign. "-142,00"
          under a heading that says "still owed" asks the reader to invert the
          sentence themselves, and the reader most likely to get that wrong is
          the one who came here worried about a debt. */}
      <div className="bg-card">
        <Figure
          className={`h-full p-5 ${inCredit ? "bg-success/10" : "bg-primary/10"}`}
          label={
            closing === 0
              ? t("portal.statement.closingSettled")
              : inCredit
                ? t("portal.statement.closingCredit")
                : t("portal.statement.closing")
          }
          value={fmtAmount(Math.abs(closing), data.currency, locale)}
          hint={inCredit ? t("portal.statement.closingCreditHint") : undefined}
        />
      </div>
    </Card>
  );
}

/**
 * The ledger itself.
 *
 * Wrapped in its own scroller: five columns do not fit a phone, and a table that
 * makes the PAGE scroll sideways takes the navigation and the totals with it.
 * The minimum width keeps the columns from collapsing into unreadable stacks
 * inside that scroller.
 */
function StatementLedger({
  data, locale, t, currency,
}: { data: PortalStatement; locale: string; t: Translate; currency: string }) {
  if (data.rows.length === 0) {
    return <p className="px-5 py-10 text-center text-sm text-foreground/70">{t("portal.statement.empty")}</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[36rem] text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wider text-foreground/70">
            <th scope="col" className="px-5 py-2.5 font-semibold">{t("portal.statement.colDate")}</th>
            <th scope="col" className="px-4 py-2.5 font-semibold">{t("portal.statement.colDescription")}</th>
            <th scope="col" className="px-4 py-2.5 text-right font-semibold">{t("portal.statement.colCharge")}</th>
            <th scope="col" className="px-4 py-2.5 text-right font-semibold">{t("portal.statement.colPaid")}</th>
            <th scope="col" className="px-5 py-2.5 text-right font-semibold">{t("portal.statement.colBalance")}</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row, i) => (
            // The index is the key on purpose. A statement line is not an entity
            // and carries no id - two payments of the same amount against the
            // same invoice on the same day are two real, indistinguishable rows.
            // The list is read-only and never reordered, so position is a stable
            // identity here in a way it would not be in an editable list.
            <tr key={i} className="border-b border-border last:border-0">
              <td className="whitespace-nowrap px-5 py-3 tabular-nums text-foreground/70">{fmtDate(row.date, locale)}</td>
              <td className="px-4 py-3">{rowLabel(row, t)}</td>
              <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums">
                {row.chargeCents === null ? "" : fmtAmount(row.chargeCents, currency, locale)}
              </td>
              <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums">
                {row.creditCents === null ? "" : fmtAmount(row.creditCents, currency, locale)}
              </td>
              <td className="whitespace-nowrap px-5 py-3 text-right font-semibold tabular-nums">
                {signedMoney(row.balanceCents, currency, locale, t)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * What a statement line is about, in words.
 *
 * Built here and not sent by the server, because the server has no language: the
 * row carries a kind and the document numbers, and this is the layer that knows
 * which catalogue the reader is in.
 */
function rowLabel(row: PortalStatementRow, t: Translate): string {
  switch (row.kind) {
    case "opening":
      return t("portal.statement.rowOpening");
    case "invoice":
      return t("portal.statement.rowInvoice", { ref: row.ref ?? "" });
    case "payment":
      // `againstRef` is the invoice the money was applied to and is what the
      // reader recognises. `ref` holds the same number today, but the fallback
      // is not decoration: it is the difference between a readable line and one
      // that says "Payment against" and stops.
      return t("portal.statement.rowPayment", { ref: row.againstRef ?? row.ref ?? "" });
    case "credit_note":
      return t("portal.statement.rowCreditNote", { ref: row.ref ?? "" });
  }
}

/**
 * A balance, with a credit marked rather than negated.
 *
 * The same reasoning as the closing tile, in the space a table cell has: the
 * amount is printed positive and tagged "CR", which is how a balance in the
 * customer's favour appears on every statement their bookkeeper has ever read.
 * A bare minus sign in a column of positive numbers is the kind of detail that
 * gets skimmed past.
 */
function signedMoney(cents: number, currency: string, locale: string, t: Translate): string {
  const amount = fmtAmount(Math.abs(cents), currency, locale);
  return cents < 0 ? `${amount} ${t("portal.statement.creditShort")}` : amount;
}
